import type { NotificationSeverity } from '@backstage/plugin-notifications-common';
import type { StatusTone } from '@vivicta/ui';

/** Which slice of the inbox is on screen — drives the `read`/`saved` filters. */
export type NotificationView = 'unread' | 'all' | 'saved' | 'read';

export type SeverityFilter = NotificationSeverity | 'any';
export type CreatedAfterFilter = 'any' | '24h' | 'week';
export type SortField = 'created' | 'topic' | 'origin';

/**
 * `severityTone` from @vivicta/ui reads `low` as a passing check and colours it
 * green, which is wrong here: a low-severity notification is informational, not
 * healthy. Notifications get their own mapping.
 */
export const severityToneOf = (
  severity: NotificationSeverity | undefined,
): StatusTone => {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warn';
    default:
      return 'neutral';
  }
};

/** Only critical and high earn a badge; normal and low are the unremarkable default. */
export const isNotableSeverity = (
  severity: NotificationSeverity | undefined,
): boolean => severity === 'critical' || severity === 'high';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const createdAfterDate = (
  filter: CreatedAfterFilter,
): Date | undefined => {
  switch (filter) {
    case '24h':
      return new Date(Date.now() - DAY);
    case 'week':
      return new Date(Date.now() - 7 * DAY);
    default:
      return undefined;
  }
};

/* The API types dates as `Date`, but the client hands back parsed JSON, so at
 * runtime they are ISO strings. */
export const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

const RELATIVE_FORMAT = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'auto',
});

/** Relative up to a week, then an absolute date — "37 weeks ago" helps nobody. */
export const relativeTime = (value: Date | string): string => {
  const date = toDate(value);
  const elapsed = date.getTime() - Date.now();
  const magnitude = Math.abs(elapsed);

  if (magnitude < MINUTE) return 'just now';
  if (magnitude < HOUR) {
    return RELATIVE_FORMAT.format(Math.round(elapsed / MINUTE), 'minute');
  }
  if (magnitude < DAY) {
    return RELATIVE_FORMAT.format(Math.round(elapsed / HOUR), 'hour');
  }
  if (magnitude < 7 * DAY) {
    return RELATIVE_FORMAT.format(Math.round(elapsed / DAY), 'day');
  }
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const absoluteTime = (value: Date | string): string =>
  toDate(value).toLocaleString();
